import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React, {useMemo} from 'react';

import {Owner} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {SavingState} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import FieldTable, {
  BaseFieldTableProps,
  ClientSchemaField,
  FieldTableLoading,
} from 'registry/field/FieldTable';
import {getOwnerById, massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {ConnectionSchemaMetadataSettingsQuery} from './__generated/ConnectionSchemaMetadataSettingsQuery';
import {ConnectionSchema, ConnectionSchemaSpec} from './ConnectionSchemaConstants';
import {ConnectionSchemaFieldTableRow} from './ConnectionSchemaFieldTableRow';
import ConnectionSchemaMetadataSection from './ConnectionSchemaMetadataSection';

export function getSpecForRequest(fields: Array<ClientSchemaField>, idPrefix: string): ConnectionSchemaSpec {
  return {fields: massageFieldsForSubmit(fields), idPrefix};
}

export const CONNECTION_SCHEMA_METADATA_SETTINGS_QUERY: DocumentNode = gql`
  query ConnectionSchemaMetadataSettingsQuery @skipAudit @engComponent(name: INSTRUMENTS) {
    ...AllAssaySchemasGraphQL
    ...AllStorableSchemasGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
  ${getFragmentWrapper('AllStorableSchemas').fragment}
`;

export interface ConnectionSchemaMetadataSettingsProps {
  schemaId?: string;
  schema: ConnectionSchema | null;
  // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaFieldDependentsSummariesById: BaseFieldTableProps['schemaFieldDependentsSummariesById'];
  adapterId: string;
  setAdapterId: (adapterId: string) => void;
  ownerChoices: Array<Owner> | null;
  ownerId: string;
  setOwnerId: (ownerId: string) => void;
  name: string;
  setName: (name: string) => void;
  sqlIdentifier: string | null;
  setSqlIdentifier: (sqlIdentifier: string) => void;
  idPrefix: string | null;
  setIdPrefix: (idPrefix: string) => void;
  fields: Array<ClientSchemaField>;
  setFields: (fields: Array<ClientSchemaField>) => void;
  shouldAllowSchemaUpdates: boolean;
  handleNext?: () => void;
}

export default function ConnectionSchemaMetadataSettings(
  props: ConnectionSchemaMetadataSettingsProps
): JSX.Element {
  const queryResult = useBenchlingQuery<ConnectionSchemaMetadataSettingsQuery>(
    CONNECTION_SCHEMA_METADATA_SETTINGS_QUERY
  );
  const linkableSchemas = useMemo(() => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      const {data} = queryResult;
      return {
        assaySchemas: [
          ...graphQLArrayToREST('RunSchema', data.runSchemas),
          ...graphQLArrayToREST('RequestSchema', data.requestSchemas),
          ...graphQLArrayToREST('ResultSchema', data.resultSchemas),
        ],
        storableSchemas: [
          ...graphQLArrayToREST('ContainerSchema', data.containerSchemas),
          ...graphQLArrayToREST('BoxSchema', data.boxSchemas),
          ...graphQLArrayToREST('LocationSchema', data.locationSchemas),
          ...graphQLArrayToREST('PlateSchema', data.plateSchemas),
        ],
      };
    }
    return {
      assaySchemas: [],
      storableSchemas: [],
    };
  }, [queryResult]);

  let error = null;
  if (!props.name) {
    error = 'Schema needs a name';
  } else if (!props.idPrefix) {
    error = 'Schema needs a prefix';
  } else if (!props.adapterId) {
    error = 'Schema needs an integration adapter';
  } else if (props.fields?.some((f) => !f.name)) {
    error = 'Schema fields must all have names';
  }

  return (
    <div className="u-flex-grow u-min-width-zero">
      <div className="settingsPageContainer-section">
        <ConnectionSchemaMetadataSection
          shouldAllowSchemaUpdates={props.shouldAllowSchemaUpdates}
          isEditableWarehouseTableName={BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME}
          adapterId={props.adapterId}
          name={props.name}
          ownerId={props.ownerId}
          ownerChoices={props.ownerChoices}
          sqlIdentifier={props.sqlIdentifier}
          idPrefix={props.idPrefix}
          onAdapterIdChange={props.setAdapterId}
          onNameChange={props.setName}
          onOwnerChange={(owner) => {
            props.setOwnerId(owner.id);
          }}
          onSqlIdentifierChange={props.setSqlIdentifier}
          onIdPrefixChange={props.setIdPrefix}
          schema={props.schema}
        />
      </div>
      <div className={css(styles.fieldSection)}>
        {queryResult.type === QueryResultType.SUCCESS ? (
          <FieldTable
            schemaId={props.schema ? props.schema.id : null}
            schema={props.schema}
            schemaHasData={props.schema?.hasData}
            schemaFieldDependentsSummariesById={props.schemaFieldDependentsSummariesById}
            isBeingCreated={props.schema == null}
            isDisabled={!props.shouldAllowSchemaUpdates}
            itemClass={ConnectionSchemaFieldTableRow}
            fields={props.fields || []}
            onFieldsChange={props.setFields}
            schemaType="connection"
            storableSchemas={linkableSchemas.storableSchemas}
            assaySchemas={linkableSchemas.assaySchemas}
            savingState={SavingState.COMPLETED}
            ownerUrlId={getOwnerById(props.ownerId)?.url_identifier || ''}
            ownerId={props.ownerId}
            shouldShowIsRequired={true}
            shouldShowIsIntegration={false}
            shouldShowIsParentLink={false}
            dataTestAttrs={{component: 'ConnectionSchemaMetadataSettings', element: 'fieldTable'}}
          />
        ) : (
          <FieldTableLoading schemaType="connection" />
        )}
      </div>
      {props.handleNext && (
        <TooltipWrapper tooltipTitle={error}>
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={error != null}
            onClick={props.handleNext}
            size={ButtonSize.MEDIUM}
            text="Next"
          />
        </TooltipWrapper>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  fieldSection: {
    marginBottom: '32px',
  },
});
